
import React, { useEffect, useState, useMemo } from "react";
import toast from "react-hot-toast";
import { 
  Search, 
  Plus, 
  Edit2, 
  CheckCircle2, 
  XCircle, 
  MoreVertical,
  Activity,
  Calendar,
  FileText
} from "lucide-react";
import { GetChatGPTPrompts } from "./apis/get";
import { CreateChatGPTPrompt } from "./apis/post";
import { UpdateChatGPTPrompt } from "./apis/put";
import LoaderService from "../services/LoaderService";


interface ChatGPTPrompt {
  prompt_id: number;
  prompt_name: string;
  prompt_text: string;
  description: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const PromptEditor = () => {
  const [prompts, setPrompts] = useState<ChatGPTPrompt[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [search, setSearch] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  
  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState<ChatGPTPrompt | null>(null);
  const [formData, setFormData] = useState({
    prompt_name: "",
    prompt_text: "",
    description: "",
    is_active: true
  });

  const fetchPrompts = async () => {
    setLoading(true);
    try {
      const response = await GetChatGPTPrompts();
      if (response.status === "success") {
        setPrompts(response.data || []);
      }
    } catch (err) {
      console.error("Failed to load prompts.");
      toast.error("Failed to fetch prompts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrompts();
  }, []);

  const filteredPrompts = useMemo(() => {
    return prompts.filter((prompt) => {
      const matchesSearch = 
        prompt.prompt_name.toLowerCase().includes(search.toLowerCase()) ||
        prompt.description?.toLowerCase().includes(search.toLowerCase()) ||
        prompt.prompt_id.toString().includes(search);
      
      const matchesStatus = 
        statusFilter === "all" || 
        (statusFilter === "active" && prompt.is_active) ||
        (statusFilter === "inactive" && !prompt.is_active);
        
      return matchesSearch && matchesStatus;
    });
  }, [prompts, search, statusFilter]);

  const handleOpenModal = (prompt: ChatGPTPrompt | null = null) => {
    if (prompt) {
      setEditingPrompt(prompt);
      setFormData({
        prompt_name: prompt.prompt_name,
        prompt_text: prompt.prompt_text,
        description: prompt.description || "",
        is_active: prompt.is_active
      });
    } else {
      setEditingPrompt(null);
      setFormData({
        prompt_name: "",
        prompt_text: "",
        description: "",
        is_active: true
      });
    }
    setIsModalOpen(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    LoaderService.show();
    try {
      let response;
      if (editingPrompt) {
        response = await UpdateChatGPTPrompt(editingPrompt.prompt_id, formData);
      } else {
        response = await CreateChatGPTPrompt(formData);
      }

      if (response.status === "success") {
        toast.success(editingPrompt ? "Prompt updated" : "Prompt created");
        setIsModalOpen(false);
        fetchPrompts();
      }
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to save prompt");
    } finally {
      LoaderService.hide();
    }
  };

  const toggleStatus = async (prompt: ChatGPTPrompt) => {
    LoaderService.show();
    try {
      const response = await UpdateChatGPTPrompt(prompt.prompt_id, {
        ...prompt,
        is_active: !prompt.is_active
      });
      if (response.status === "success") {
        toast.success(`Prompt ${!prompt.is_active ? 'enabled' : 'disabled'}`);
        fetchPrompts();
      }
    } catch (error) {
      toast.error("Failed to update status");
    } finally {
      LoaderService.hide();
    }
  };

  return (
    <div className="p-8 min-h-screen bg-slate-950 text-slate-200">
      {/* Header section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent">
            ChatGPT Prompts Manager
          </h1>
          <p className="text-slate-400 mt-1">Configure and manage AI system prompts</p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-xl transition-all shadow-lg shadow-blue-500/20 active:scale-95"
        >
          <Plus size={18} />
          Create Prompt
        </button>
      </div>

      {/* Filters bar */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4 mb-8 flex flex-wrap items-center gap-4 shadow-xl">
        <div className="relative flex-1 min-w-[300px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={20} />
          <input
            type="text"
            placeholder="Search by name, ID or description..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-11 pr-4 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/40 transition-all"
          />
        </div>
        
        <div className="flex items-center gap-3">
          <div className="flex bg-slate-950 border border-slate-800 p-1 rounded-xl">
            {['all', 'active', 'inactive'].map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-4 py-1.5 rounded-lg text-sm font-medium capitalize transition-all ${
                  statusFilter === status 
                    ? 'bg-slate-800 text-white shadow-lg' 
                    : 'text-slate-500 hover:text-slate-300'
                }`}
              >
                {status}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content section */}
      {loading && prompts.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          <div className="w-12 h-12 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin"></div>
          <p className="text-slate-400">Loading prompts...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredPrompts.map((prompt) => (
            <div 
              key={prompt.prompt_id}
              className="group bg-slate-900/50 backdrop-blur-xl border border-slate-800/60 rounded-2xl p-6 shadow-xl hover:border-blue-500/30 transition-all"
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                    prompt.is_active ? 'bg-emerald-500/10 text-emerald-400' : 'bg-rose-500/10 text-rose-400'
                  }`}>
                    {prompt.is_active ? <CheckCircle2 size={22} /> : <XCircle size={22} />}
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-100 group-hover:text-blue-400 transition-colors">
                      {prompt.prompt_name}
                    </h3>
                    <span className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">ID: {prompt.prompt_id}</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button 
                    onClick={() => handleOpenModal(prompt)}
                    className="p-2 bg-slate-800 rounded-lg text-slate-400 hover:text-blue-400 hover:bg-slate-700 transition-all"
                    title="Edit Prompt"
                  >
                    <Edit2 size={16} />
                  </button>
                  <button 
                    onClick={() => toggleStatus(prompt)}
                    className={`p-2 rounded-lg transition-all ${
                      prompt.is_active 
                        ? 'bg-rose-500/10 text-rose-400 hover:bg-rose-500/20' 
                        : 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20'
                    }`}
                    title={prompt.is_active ? "Disable" : "Enable"}
                  >
                    <Activity size={16} />
                  </button>
                </div>
              </div>

              <p className="text-sm text-slate-400 line-clamp-2 mb-6 h-10">
                {prompt.description || "No description provided."}
              </p>

              <div className="space-y-3">
                <div className="p-3 bg-slate-950 rounded-xl border border-slate-800/50">
                  <div className="flex items-center gap-2 text-[10px] font-bold text-slate-500 uppercase tracking-tighter mb-1.5">
                    <FileText size={12} />
                    Prompt Content
                  </div>
                  <p className="text-xs text-slate-300 line-clamp-3 italic">"{prompt.prompt_text}"</p>
                </div>
                
                <div className="flex items-center justify-between pt-2 border-t border-slate-800/50">
                  <div className="flex items-center gap-2 text-[10px] text-slate-500">
                    <Calendar size={12} />
                    Updated: {new Date(prompt.updated_at).toLocaleDateString()}
                  </div>
                  <div className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase ${
                    prompt.is_active ? 'bg-emerald-500/10 text-emerald-400' : 'bg-rose-500/10 text-rose-400'
                  }`}>
                    {prompt.is_active ? 'Active' : 'Inactive'}
                  </div>
                </div>
              </div>
            </div>
          ))}
          
          {filteredPrompts.length === 0 && (
            <div className="col-span-full py-20 bg-slate-900/30 border border-dashed border-slate-800 rounded-3xl flex flex-col items-center justify-center text-slate-500">
              <FileText size={48} className="opacity-20 mb-4" />
              <p className="text-lg">No prompts found matching your criteria</p>
              <button onClick={() => { setSearch(""); setStatusFilter("all"); }} className="text-blue-500 hover:underline mt-2">Clear all filters</button>
            </div>
          )}
        </div>
      )}

      {/* Editor Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-md" onClick={() => setIsModalOpen(false)}></div>
          <div className="relative bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="px-8 py-6 border-b border-slate-800 flex justify-between items-center bg-slate-900/50">
              <div>
                <h2 className="text-xl font-bold text-white">
                  {editingPrompt ? 'Edit System Prompt' : 'Create New Prompt'}
                </h2>
                <p className="text-sm text-slate-500 mt-1">Configure ChatGPT behavior for the system</p>
              </div>
              <button 
                onClick={() => setIsModalOpen(false)}
                className="w-10 h-10 flex items-center justify-center rounded-xl bg-slate-800 text-slate-400 hover:text-white transition-colors"
              >
                <MoreVertical size={20} />
              </button>
            </div>

            <form onSubmit={handleSave} className="p-8 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-300 ml-1">Prompt Name</label>
                  <input
                    required
                    type="text"
                    value={formData.prompt_name}
                    onChange={(e) => setFormData({...formData, prompt_name: e.target.value})}
                    placeholder="e.g., Profile Analysis"
                    className="w-full px-4 py-3 rounded-xl bg-slate-950 border border-slate-800 text-slate-100 placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                  />
                </div>
                <div className="space-y-2 flex flex-col">
                  <label className="text-sm font-semibold text-slate-300 ml-1">Status</label>
                  <button
                    type="button"
                    onClick={() => setFormData({...formData, is_active: !formData.is_active})}
                    className={`flex items-center justify-between px-4 py-3 rounded-xl border transition-all ${
                      formData.is_active 
                        ? 'bg-emerald-500/5 border-emerald-500/20 text-emerald-400' 
                        : 'bg-rose-500/5 border-rose-500/20 text-rose-400'
                    }`}
                  >
                    <span className="font-medium">{formData.is_active ? 'Active' : 'Inactive'}</span>
                    <div className={`w-2 h-2 rounded-full animate-pulse ${formData.is_active ? 'bg-emerald-400' : 'bg-rose-400'}`}></div>
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-300 ml-1">Description</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="What is this prompt used for?"
                  className="w-full px-4 py-3 rounded-xl bg-slate-950 border border-slate-800 text-slate-100 placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-300 ml-1">Prompt Text</label>
                <textarea
                  required
                  rows={6}
                  value={formData.prompt_text}
                  onChange={(e) => setFormData({...formData, prompt_text: e.target.value})}
                  placeholder="Enter the system instruction for ChatGPT..."
                  className="w-full px-4 py-3 rounded-xl bg-slate-950 border border-slate-800 text-slate-100 placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500/40 resize-none"
                />
                <p className="text-[10px] text-slate-500 italic ml-1">* Use {`{variables}`} for dynamic data injection if supported by the backend.</p>
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="flex-1 py-3 bg-slate-800 hover:bg-slate-700 text-white font-semibold rounded-2xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-2 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-bold rounded-2xl transition-all shadow-lg shadow-blue-500/25 active:scale-95 px-12"
                >
                  {editingPrompt ? 'Update Prompt' : 'Save New Prompt'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromptEditor;